<template>
  <svg :width="`${matOuterWidth}in`" :height="`${matOuterHeight}in`" xmlns="http://www.w3.org/2000/svg">
    <defs>
      <!-- Define a mask using the PNG -->
      <mask v-for="(artwork, index) in frameSpec.artworks" :key="index" :id="`${uuid}-pngMask`">
        <!-- Use the PNG image to define the mask -->
        <image
          :x="artworkOffsetX + openingOffset(index).leftInInches"
          :y="artworkOffsetY + openingOffset(index).topInInches"
          :width="artworkWidthInInches"
          :height="artworkHeightInInches"
          preserveAspectRatio="none"
          :href="imageUrl"
        />
      </mask>

      <!-- Define a filter for the drop shadow -->
      <filter :id="`${uuid}-dropShadow`" x="0" y="0" :width="matOuterWidth" :height="matOuterHeight" filterUnits="userSpaceOnUse">
        <feGaussianBlur in="SourceAlpha" :stdDeviation="dropShadow.blur" />
        <feOffset :dx="-dropShadow.offset" :dy="dropShadow.offset" result="offsetblur" />
        <feMerge>
          <feMergeNode in="offsetblur" />
          <feMergeNode in="SourceGraphic" />
        </feMerge>
      </filter>
    </defs>

    <!-- Backing board component -->
    <RenderSvgBackingBoard
      :frameSpec="frameSpec"
      :widthInInches="matOuterWidth"
      :heightInInches="matOuterHeight"
      :x="0"
      :y="0"
    />
    

    <!-- Apply the mask and drop shadow to the shape -->
    <g v-if="floatedMount" :style="`filter: url(#${uuid}-dropShadow)`">
      <rect
        v-if="imageIsPng"
        :x="artworkOffsetX"
        :y="artworkOffsetY"
        :width="artworkWidthInInches"
        :height="artworkHeightInInches"
        :mask="`url(#${uuid}-pngMask)`"
        fill="rgb(0, 0, 0, 0.5)"
      />
      <rect
        v-else
        :x="artworkOffsetX"
        :y="artworkOffsetY"
        :width="artworkWidthInInches"
        :height="artworkHeightInInches"
        fill="rgb(0, 0, 0, 0.5)"
      />
    </g>

    <!-- Artwork component -->
    <RenderSvgArtwork
      v-for="(artwork, artworkKey) in frameSpec.artworks"
      :key="artworkKey"
      :artworkKey="artworkKey"
      :frameSpec="frameSpec"
      :moulding="moulding"
      :x="artworkOffsetX + openingOffset(artworkKey).leftInInches"
      :y="artworkOffsetY + openingOffset(artworkKey).topInInches"
      :allowUpload="allowUpload"
      :loading="loading[artworkKey]"
      :readonly="readonly"
      @upload-click="$emit('upload-click')"
      @click="onArtworkClick($event, artworkKey)"
      @drop.stop="emitArtworkDrop($event, artworkKey)"
    />

    <!-- Mat components -->
    <RenderSvgMat
      v-for="(mat, index) in frameSpec.mats.slice().reverse()"
      :key="index"
      :frameSpec="frameSpec"
      :x="0"
      :y="0"
      :mat="mat"
      :matIndex="index"
      :templatePreview="templatePreview"
    />
  </svg>
</template>

<script>
import RenderSvgArtwork from './RenderSvgArtwork.vue';
import RenderSvgMat from './RenderSvgMat.vue';
import RenderSvgBackingBoard from './RenderSvgBackingBoard.vue';
import { uploadedArtworkMatOverlap } from "../../hardCodedValues";
import uuidv4 from "uuid/v4";

export default {
  components: {
    RenderSvgArtwork,
    RenderSvgMat,
    RenderSvgBackingBoard
  },
  props: {
    frameSpec: {
      type: Object,
      required: true
    },
    allowUpload: {
      type: Boolean,
      required: false,
      default: false
    },
    artworkUrl: {
      type: String,
      required: false
    },
    loading: {
      type: Array,
      default: () => []
    },
    readonly: {
      type: Boolean,
      default: false
    },
    inBundle: {
      type: Object,
      required: false
    },
    moulding: {
      type: Object,
      required: false
    },
    templatePreview: {
      type: Boolean,
      default: false
    },
  },
  inject: ['emitArtworkClick', 'emitArtworkDrop'],
  computed: {
    fullMat () {
      return this.frameSpec.getFullPrimaryMat();
    },
    openings () {
      return this.frameSpec.matTemplate?.openings
        || (this.fullMat && this.fullMat.openings)
        || [];
    },
    uuid() {
      return uuidv4();
    },
    artwork() {
      return this.frameSpec.getArtwork() || {};
    },
    floatedMount() {
      return this.frameSpec.isFloated();
    },
    dropShadow() {
      const flush = this.frameSpec.isFlushFloated() && !this.frameSpec.isClearFloated();
      return flush ? { blur: 0.05, offset: 0.05 } : { blur: 0.1, offset: 0.1 };
    },
    matImage () {
      return this.fullMat && this.fullMat.matAsset ? this.fullMat.matAsset : null;
    },
    imageIsPng() {
      if (!this.frameSpec) return "";

      const customerSpecifiedHasPng = this.artwork.customerSpecified && this.artwork.customerSpecified.url.includes(".png");
      const originalUneditedHasPng = this.artwork.originalUnedited && this.artwork.originalUnedited.url.includes(".png");
      return customerSpecifiedHasPng || originalUneditedHasPng;
    },
    imageUrl() {
      if (!this.frameSpec) return "";

      const croppedAndNormalizedUrl = this.artwork.croppedAndNormalized && this.artwork.croppedAndNormalized.url;
      const customerSpecifiedUrl = this.artwork.customerSpecified && this.artwork.customerSpecified.url;

      // Digital PNGs print on white paper, so use the opaque JPEG (customerSpecified)
      // rather than the transparent croppedAndNormalized PNG.
      if (this.imageIsPng && this.frameSpec.isDigital()) {
        return customerSpecifiedUrl || croppedAndNormalizedUrl;
      }

      if (this.imageIsPng || !customerSpecifiedUrl) {
        return croppedAndNormalizedUrl;
      }

      return customerSpecifiedUrl;
    },
    isCanvasMoulding() {
      return this.frameSpec.isCanvasOrCanvasPaper();
    },
    reveal() {
      return this.frameSpec.getRevealOrZero();
    },
    matOuterWidth() {
      if (this.fullMat && this.fullMat.matWidth) {
        return this.fullMat.matWidth;
      }
      return this.frameSpec.matOuterSize().width || 0;
    },
    matOuterHeight() {
      if (this.fullMat && this.fullMat.matHeight) {
        return this.fullMat.matHeight;
      }
      return this.frameSpec.matOuterSize().height || 0;
    },
    artworkWidthInInches() {
      return this.artwork.exterior.widthInInches;
    },
    artworkHeightInInches() {
      return this.artwork.exterior.heightInInches;
    },
    artworkOffsetX() {
      if (this.isCanvasMoulding || this.matImage || !this.frameSpec.shouldHaveMatOverlappingArtwork()) {
        return this.reveal.leftInInches;
      } else {
        return this.reveal.leftInInches - (uploadedArtworkMatOverlap / 2);
      } 
    },
    artworkOffsetY() {
      if (this.isCanvasMoulding || this.matImage || !this.frameSpec.shouldHaveMatOverlappingArtwork()) {
        return this.reveal.topInInches;
      } else {
        return this.reveal.topInInches - (uploadedArtworkMatOverlap / 2);
      }  
    },
  },
  methods: {
    openingOffset(artworkKey) {
      return this.openings[artworkKey] || { leftInInches: 0, topInInches: 0 };
    },
    onArtworkClick(ev, artworkKey) {
      if (this.inBundle) {
        ev.stopPropagation();
      }

      this.emitArtworkClick(artworkKey);
    }
  }
};
</script>

<style lang="scss" scoped>
svg {
  align-items: center;
  display: flex;
  height: auto;
  justify-content: center;
  max-width: 100%;
  width: 100%;
}
</style>